import React, { useState, useEffect } from 'react';
import { HashRouter, Route, Routes } from 'react-router-dom';
import './App.css';
import { AuthContext, AuthInformation } from './auth/AuthContext';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { NavigatorPane } from './components/NavigatorPane';
import {createFetchRequest} from "./util/fetcher";

interface AuthVerificationStatus {
  isVerifying: boolean;
  lastVerification: Date | undefined;
  authInterval: NodeJS.Timer | null
}

function App() {
  const [verificationState, setLastVerification] = useState<AuthVerificationStatus>({
    isVerifying: false,
    lastVerification: undefined,
    authInterval: null
  });
  const [authDetails, setAuth] = useState<AuthInformation>({
    sessionId: sessionStorage.getItem('sessionId') ?? "",
    name: sessionStorage.getItem('name') ?? "",
    isLoggedIn: Boolean(sessionStorage.getItem('sessionId'))
  });

  const applyAuth = ({sessionId, name}: AuthInformation): void => {
    sessionStorage.setItem('sessionId', sessionId);
    sessionStorage.setItem('name', name );
    setAuth({ sessionId, name, isLoggedIn: true });
  };

  const signOut = (): void => {
    sessionStorage.removeItem('sessionId');
    sessionStorage.removeItem('name');
    setAuth({
      name: '',
      sessionId: '',
      isLoggedIn: false
    });
  }

  useEffect(() => {
    if (!authDetails.sessionId) {
      return;
    }
    if (verificationState.isVerifying) {
      return;
    }
    if (verificationState.lastVerification !== undefined && verificationState.lastVerification > new Date(new Date().valueOf() - 60000 * 15) ) {
      // if it's set, but last verification was within 15 minutes, then we skip
      return;
    }
    if (verificationState.authInterval) {
      clearInterval( verificationState.authInterval );
    }
    setLastVerification({
      ...verificationState,
      isVerifying: true,
      authInterval: null
    });
    async function doLoginRequest( session: string ): Promise<void> {
      try {
        const res = await createFetchRequest( 'api/login/ping', session );
        if (res.status !== 204) {
          signOut();
          return;
        }
      } catch (e) {
        signOut();
      }
    }

    void doLoginRequest(authDetails.sessionId)
      .then(() => setLastVerification({
        isVerifying: false,
        lastVerification: new Date(),
        authInterval: setInterval( () => {
          if (authDetails.sessionId) {
            doLoginRequest( authDetails.sessionId );
          }
        }, 15 * 1000 * 60 )
      }));

      return () => {
        if (verificationState.authInterval) {
          clearInterval(verificationState.authInterval);
        }
      }

  }, [authDetails.sessionId, verificationState]);


  return (
    <div className="app">
      <AuthContext.Provider value={ { ...authDetails, signOut } }>
        <HashRouter>
          <Routes>
            <Route
              path="/:deeplink?"
              element={<ProtectedRoute signinCallback={applyAuth}><NavigatorPane /></ProtectedRoute>}
            />
          </Routes>
        </HashRouter>
      </AuthContext.Provider>
    </div>
  );
}

export default App;
